/**
 * Data panel component
 */

/** Dependencies -------------------------------------------- */

// External

import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

// Internal

import { getPlugin, developmentTypes, developmentLevels } from '../dictionary'
import { useAppState } from '../state'

import Button from './button'
import KeywordSearch from './keywordSearch'
import Tooltip from './tooltip'
import DevelopmentList from './developmentList'
import EmptyDevelopment from './emptyDevelopment'

/** Main ---------------------------------------------------- */

const baseClassName = 'data-panel'

export default function DataPanel() {

    // Constants

    const plugin = getPlugin()

    const aggregate = useAppState( ( state ) => state.aggregate )
    const setAggregate = useAppState( ( state ) => state.setAggregate )
    const developmentTypeFilter = useAppState( ( state ) => state.developmentTypeFilter )
    const filterByDevelopmentType = useAppState( ( state ) => state.filterByDevelopmentType )
    const developmentLevelFilter = useAppState( ( state ) => state.developmentLevelFilter )
    const filterByDevelopmentLevel = useAppState( ( state ) => state.filterByDevelopmentLevel )
    const developments = useAppState( ( state ) => state.developments )
    const development = useAppState( ( state ) => state.development )
    const setDevelopment = useAppState( ( state ) => state.setDevelopment )

    const showBackButton = aggregate
    const backButtonTooltipContent = development ? `Up to ${aggregate.title}` : `Up to United States`
    const title = aggregate ? aggregate.title : 'United States'

    const showControls = ! development
    const instructions = aggregate
        ? `Select one of the options below to read election developments documented in ${aggregate.title}.`
        : 'Select a state on the map or one of the options below to read about election developments.'

    const isEmpty = ! developments.length

    const consolidatedClassName = classNames(
        baseClassName,
        { [`has-development-level-${aggregate?.developmentLevel.id}`]: aggregate },
        { 'is-empty': isEmpty },
    )

    // Handlers

    function handleDevelopmentTypeChange( e ) {
        filterByDevelopmentType( e.target.value )
    }

    function handleDevelopmentLevelChange( e ) {
        filterByDevelopmentLevel( e.target.value )
    }

    function handleBackButtonClick() {

        if ( development ) {
            setDevelopment( null )
            return
        }
        
        setAggregate( null )
    }

    return (
        <div className={ consolidatedClassName }>

            <header className={ `${baseClassName}__header` }>

                <div className={ `${baseClassName}__top-bar` }>

                    { showBackButton &&
                        <Button 
                            title="Up"
                            size="sm"
                            leadingIconProps={ { type: 'arrowLeft' } }
                            className={ `${baseClassName}__back-button` }
                            onClick={ handleBackButtonClick }
                            data-tooltip-id="data-panel-back"
                        />
                    }

                    { showBackButton && <Tooltip id="data-panel-back" place="top">{ backButtonTooltipContent }</Tooltip> }
                
                    <h2 className={ `${baseClassName}__title` }>
                        { title }
                    </h2>

                </div>

                { showControls &&
                    <div className={ `${baseClassName}__controls` }>
                        <p className={ `${baseClassName}__instructions` }>
                            { instructions }
                        </p>
                        <form className={ `${baseClassName}__filters form is-layout-single-line` }>

                            { plugin.keywords.length &&
                                <fieldset className="form__fieldset">
                                    <legend className="form__legend">Search developments by keywords</legend>
                                    <div className="form__field is-type-search">
                                        <KeywordSearch/>
                                    </div>
                                </fieldset>
                            }

                            <fieldset className="form__fieldset">
                                <legend className="form__legend">Filter and sort developments</legend>
                                <div className="form__field is-type-select">
                                    <label className="form__label" htmlFor="filter-development-type">Filter by development type</label>
                                    <select id="filter-development-type" value={ developmentTypeFilter ? developmentTypeFilter.id : '' } onChange={ handleDevelopmentTypeChange }>
                                        <option value="">All types</option>
                                        { developmentTypes.map( ( developmentType ) => (
                                            <option key={ developmentType.id } value={ developmentType.id }>{ developmentType.label }</option>
                                        ) ) }
                                    </select>
                                </div>
                                <div className="form__field is-type-select">
                                    <label className="form__label" htmlFor="filter-development-level">Filter by development level</label>
                                    <select id="filter-development-level" value={ developmentLevelFilter ? developmentLevelFilter.id : '' } onChange={ handleDevelopmentLevelChange }>
                                        <option value="">All levels</option>
                                        { developmentLevels.map( ( developmentLevel ) => (
                                            <option key={ developmentLevel.id } value={ developmentLevel.id }>{ developmentLevel.label }</option>
                                        ) ) }
                                    </select>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                }

            </header>

            <div className={ `${baseClassName}__data` }>
                { isEmpty ? <EmptyDevelopment/> : <DevelopmentList/> }
            </div>

        </div>
    )
}
